import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    GetWorkflowDetailsResult, StartRecipeExecutionResultV1
} from "@amzn/sm-workflow-orchestration-service-js-client/lib/smworkfloworchestrationservicelambda";
import {AjvError} from "@rjsf/core";
import {SelectProps} from "@amzn/awsui-components-react-v3/polaris/select/interfaces";
import {
    pleaseSelectLabel,
    TARGETED_WEBSITES
} from "src/constants/workflow-instance";
import {WorkflowInstanceUtil} from "src/utils/workflow-instance-util";
import {toast} from "react-toastify";
import {DomainTracker} from "src/model/DomainTracker";
import {UserInputError} from "src/components/common/error-message-helper";
import {BaseChangeDetail} from "@amzn/awsui-components-react-v3/polaris/input/interfaces";
import {useQuery} from "@tanstack/react-query";
import {hasJsonKey} from "src/utils/string-helper";
import {CapabilityService} from "src/model/CapabilityService";
import {sortArrayAlphabetically} from "src/utils/array-helper";

export const useStartScreenUtil = () => {
    const [schemaLoading, setSchemaLoading] = useState<boolean>(false)
    const [startWFLoading, setStartWFLoading] = useState<boolean>(false)
    const [workflowDetails, setWorkflowDetails] = useState<GetWorkflowDetailsResult>({})
    const [errorListMap, setErrorListMap] = useState<{ [key: string]: AjvError[] }>({})
    const [enableLiveValidate, setEnableLiveValidate] = useState<boolean>(false)
    const errorListMapTemp: { [key: string]: AjvError[] } = {}
    const [workflowInput, setWorkflowInput] = useState<{ [key: string]: any }>({})
    const [adhocStartRecipeExecutionsResult, setAdhocStartRecipeExecutionsResult] =
        useState<StartRecipeExecutionResultV1>({ executionId: undefined, errorMessage: undefined });
    const itemsRef = useRef<Array<HTMLButtonElement | null>>([]);

    const [classificationTagSelectedOption, setClassificationTagSelectedOption] = React.useState<SelectProps.Option>(pleaseSelectLabel);

    const [selectedSubClassificationTag, setSelectedSubClassificationTag] = React.useState<SelectProps.Option>(pleaseSelectLabel);
    const [domainName, setDomainName] = React.useState<string>("");
    const [isContinuous] = React.useState<boolean>(true);
    const [capabilitySelectedOption, setCapabilitySelectedOption] = useState<SelectProps.Option>(pleaseSelectLabel);
    const [capabilityOptions, setCapabilityOptions] = useState<SelectProps.Options>([]);
    const [primitiveSelectedOption, setPrimitiveSelectedOption] = useState<SelectProps.Option>(pleaseSelectLabel);
    const [workflowDefinitionOption, setWorkflowDefinitionOption] = useState<SelectProps.Option>(pleaseSelectLabel);
    const targetWebsiteOptionsDefinitions: Array<SelectProps.Option> = [...TARGETED_WEBSITES.map(targetWebsite => {
        return {
            label: targetWebsite,
            value: targetWebsite
        }
    })
    ]
    const [targetWebsite, setTargetWebsite] = React.useState<SelectProps.Option>(targetWebsiteOptionsDefinitions[0]);
    const [targetWebsiteOptions, setTargetWebsiteOptions] = useState<Array<SelectProps.Option>>(targetWebsiteOptionsDefinitions)

    const [isScheduled, setIsScheduled] = React.useState<boolean>(false);
    const [scheduleName, setScheduleName] = React.useState<string>("");
    const [cronExpression, setCronExpression] = React.useState<string>("");

    /* istanbul ignore next */
    const {
        data: workflowInputTemplate,
        isLoading: workflowDefaultLoading,
        error: workflowDefaultLoadError
    } = useQuery({
        queryKey: [`getWorkflowInputTemplate-${capabilitySelectedOption?.value}-${primitiveSelectedOption?.value}-${workflowDefinitionOption?.value}`],
        queryFn: () => {
            const workflowInputTemplateId = `${capabilitySelectedOption?.value}-${primitiveSelectedOption?.value}-${workflowDefinitionOption?.value}`;
            return new DomainTracker().getWorkflowInputTemplate(workflowInputTemplateId);
        },
        enabled: !!capabilitySelectedOption?.value && !!primitiveSelectedOption?.value && !!workflowDefinitionOption?.value
    });

    useEffect(() => {
        itemsRef.current = itemsRef.current.slice(0, Object.keys(workflowDetails.stageInstanceInputSchemaMap || {}).length);
    }, []);

    useEffect(() => {
        workflowInputTemplate && setWorkflowInput(JSON.parse(workflowInputTemplate?.defaultInputJSON || "{}"));
    }, [workflowInputTemplate, setWorkflowInput])


    const workflowUISchema = useMemo(() => {
        return workflowInputTemplate?.defaultUiSchemaJSON && hasJsonKey(workflowInputTemplate?.defaultUiSchemaJSON) ? JSON.parse(workflowInputTemplate?.defaultUiSchemaJSON) : workflowDetails.startUiSchemaMap
    }, [workflowInputTemplate, workflowDetails])

    const { data: capabilitiesData } = CapabilityService.useCapabilities();

    useEffect(() => {
        if (capabilitiesData) {
            const uniqueCapabilities = Array.from(new Set(capabilitiesData.map(cap => cap.capabilityId)));
            setCapabilityOptions(sortArrayAlphabetically(uniqueCapabilities).map(cap => ({
                value: cap,
                label: cap
            })));
        }
    }, [capabilitiesData]);

    function retrieveSchemaForWorkflowDefinition(workflowDefinition?: string) {
        if ((workflowDefinition === undefined) || (workflowDefinition === "")) {
            toast.error("Workflow Definition can not be blank")
            return
        }
        setSchemaLoading(true)
        new DomainTracker().getWorkflowDetails(workflowDefinition)
            .then(resp => {
                itemsRef.current = itemsRef.current.slice(0, Object.keys(resp.stageInstanceInputSchemaMap || {}).length);
                setWorkflowDetails({
                    stageInstanceInputSchemaMap: resp.stageInstanceInputSchemaMap,
                    startUiSchemaMap: resp.startUiSchemaMap,
                    stages: resp.stages
                })
            })
            .catch(err => {
                console.error("Get Workflow Details Error", err)
                toast.error("Failed to retrieve workflow details")
                setWorkflowDetails({})
            })
            .then(() => {
                setSchemaLoading(false)
            })
    }

    /**
     * callback function responsible for updating component's internal state of user form input data(workflowInput).
     * The state value holds the form data entered by user for every field.
     * triggered when user updates any field in the input form.
     */
    const onTabFormDataChange = useCallback((formInput, tabId: string) => {
        setAdhocStartRecipeExecutionsResult({
            ...adhocStartRecipeExecutionsResult,
            executionId: undefined,
            errorMessage: undefined
        })
        setWorkflowInput(prevWorkflowInput => ({...prevWorkflowInput, [tabId]: formInput.formData}))
    }, [
        setAdhocStartRecipeExecutionsResult,
        adhocStartRecipeExecutionsResult,
        setWorkflowInput,
        workflowInput])


    /**
     * callback function responsible for updating component's internal state for user form input errors(errorListMap).
     * The state value holds the errors for any invalid form field entry.
     * triggered when user updates any invalid entry in the input form.
     * The errors are collected on state & displayed to user collectively on form submission attempt.
     */
    const onTabFormDataError = useCallback((err: AjvError[], tabId: string) => {
        errorListMapTemp[tabId] = err
        setErrorListMap(errorListMapTemp)
    }, [errorListMapTemp, setErrorListMap])

    const onTabDataChangeHandler = (tabId: string, tabIndex: number, callback: () => void) => {
        delete errorListMapTemp[tabId]
        setErrorListMap(errorListMapTemp)
        if (tabIndex + 1 == Object.keys(workflowDetails.stageInstanceInputSchemaMap || {}).length) {
            if (Object.keys(errorListMapTemp).length == 0) {
                callback();
            }
        }
    }

    const onFormSubmitHandler = (callback: () => void) => {
        setEnableLiveValidate(true)
        const stageInstanceInputSchemaMap = Object.entries(workflowDetails.stageInstanceInputSchemaMap || {});
        if (stageInstanceInputSchemaMap.length) {
            stageInstanceInputSchemaMap.forEach(([key, value], index) => {
                itemsRef.current[index]?.click();
            })
        } else {
            callback();
        }
    }

    const errorText = React.useMemo(() => {
        return (Object.keys(errorListMap).length > 0 || adhocStartRecipeExecutionsResult.errorMessage) && (
            <div>
                <UserInputError errorListMap={errorListMap}/>
                {adhocStartRecipeExecutionsResult.errorMessage && (
                    <p>{adhocStartRecipeExecutionsResult.errorMessage}</p>
                )}
            </div>
        );
    }, [errorListMap, adhocStartRecipeExecutionsResult.errorMessage]);

    const selectedCapability = useMemo(() => {
        if (!capabilitiesData || !capabilitySelectedOption?.value || !primitiveSelectedOption?.value) {
            return null;
        }
        return capabilitiesData.find(cap =>
            cap.capabilityId === capabilitySelectedOption.value &&
            cap.primitiveId === primitiveSelectedOption.value
        );
    }, [capabilitiesData, capabilitySelectedOption?.value, primitiveSelectedOption?.value]);

    const primitiveOptions = useMemo(() => {
        if (!capabilitiesData || !capabilitySelectedOption?.value) return [];

        const primitives = Array.from(
            new Set(
                capabilitiesData
                    .filter(cap => cap.capabilityId === capabilitySelectedOption.value)
                    .map(cap => cap.primitiveId)
            )
        );
        return sortArrayAlphabetically(primitives).map(prim => ({
            value: prim,
            label: prim
        }));
    }, [capabilitiesData, capabilitySelectedOption?.value]);

    const workflowDefinitionOptions = useMemo(() => {
        if (!selectedCapability) return [];

        const workflows = selectedCapability.workflows || [];
        const sortedWorkflows = sortArrayAlphabetically(workflows.map(w => w.recipeName));
        return sortedWorkflows.map(w => ({
            value: w,
            label: w
        }));
    }, [selectedCapability, capabilitySelectedOption.value, primitiveSelectedOption.value]);

    const selectedWorkflow = useMemo(() => {
        if (!selectedCapability || !workflowDefinitionOption?.value) return null;

        return selectedCapability.workflows?.find(w =>
            w.recipeName === workflowDefinitionOption.value
        );
    }, [selectedCapability, workflowDefinitionOption?.value]);

    const classificationTagOptions = useMemo(() => {
        if (!selectedWorkflow) return [];

        const classificationTags = selectedWorkflow?.allowedTags?.classificationTag || [];
        return sortArrayAlphabetically(classificationTags).map(tag => ({
            value: tag,
            label: tag
        }));
    }, [selectedWorkflow, workflowDefinitionOption?.value]);

    const subClassificationTagOptions = useMemo(() => {
        if (!selectedCapability || !workflowDefinitionOption?.value || !classificationTagSelectedOption?.value) {
            return [];
        }
        const workflow = selectedCapability.workflows?.find(w => w.recipeName === workflowDefinitionOption.value);
        const subClassificationTags = workflow?.allowedTags?.subClassificationTag || [];
        const relatedSubClassificationTags = workflow?.tagRelationshipMap?.[classificationTagSelectedOption.value] || [];
        const validSubClassificationTags = subClassificationTags.filter(tag => relatedSubClassificationTags.includes(tag));
        return sortArrayAlphabetically(validSubClassificationTags).map(tag => ({
            value: tag,
            label: tag
        }));
    }, [selectedWorkflow, classificationTagSelectedOption?.value]);

    const isScheduleDisabled = useMemo(() => {
        if (!classificationTagSelectedOption) return false;
        return WorkflowInstanceUtil.isProgramScheduleDisabled(classificationTagSelectedOption);
    }, [classificationTagSelectedOption]);

    useEffect(() => {
        if (primitiveOptions.length === 1) {
            setPrimitiveSelectedOption(primitiveOptions[0]);
        }
    }, [primitiveOptions]);

    useEffect(() => {
        if (workflowDefinitionOptions.length === 1) {
            setWorkflowDefinitionOption(workflowDefinitionOptions[0]);
        }
    }, [workflowDefinitionOptions]);

    useEffect(() => {
        if (classificationTagOptions.length === 1) {
            setClassificationTagSelectedOption(classificationTagOptions[0]);
        }
    }, [classificationTagOptions]);

    useEffect(() => {
        if (workflowDefinitionOption?.value) {
            retrieveSchemaForWorkflowDefinition(workflowDefinitionOption.value);
        }
    }, [workflowDefinitionOption?.value]);

    const onCapabilityChangeHandler = useCallback((detail: SelectProps.ChangeDetail) => {
        setCapabilitySelectedOption(detail.selectedOption);
        setPrimitiveSelectedOption(pleaseSelectLabel);
        setWorkflowDefinitionOption(pleaseSelectLabel);
        setClassificationTagSelectedOption(pleaseSelectLabel);
        setSelectedSubClassificationTag(pleaseSelectLabel);
    }, []);

    const onPrimitiveChangeHandler = useCallback((detail: SelectProps.ChangeDetail) => {
        setPrimitiveSelectedOption(detail.selectedOption);
        setWorkflowDefinitionOption(pleaseSelectLabel);
        setClassificationTagSelectedOption(pleaseSelectLabel);
        setSelectedSubClassificationTag(pleaseSelectLabel);
    }, []);

    const onWorkflowDefinitionChangeHandler = useCallback((detail: SelectProps.ChangeDetail) => {
        if (!detail.selectedOption.value || !capabilitySelectedOption.value || !primitiveSelectedOption.value) {
            toast.error("Please select Capability and Primitive first");
            return;
        }
        setWorkflowDefinitionOption(detail.selectedOption);
        setClassificationTagSelectedOption(pleaseSelectLabel);
        setSelectedSubClassificationTag(pleaseSelectLabel);
    }, [capabilitySelectedOption.value, primitiveSelectedOption.value]);

    const onClassificationTagChangeHandler = useCallback((detail: SelectProps.ChangeDetail) => {
        if (!capabilitySelectedOption.value || !primitiveSelectedOption.value || !workflowDefinitionOption.value) {
            toast.error("Please select Capability, Primitive, and Workflow Definition first");
            return;
        }
        setClassificationTagSelectedOption(detail.selectedOption);
    }, [capabilitySelectedOption.value, primitiveSelectedOption.value, workflowDefinitionOption.value]);

    const resetFormValues = useCallback(() => {
        setWorkflowInput({})
        setCapabilitySelectedOption(pleaseSelectLabel)
        setPrimitiveSelectedOption(pleaseSelectLabel)
        setWorkflowDefinitionOption(pleaseSelectLabel)
        setClassificationTagSelectedOption(pleaseSelectLabel)
        setScheduleName("")
        setCronExpression("")
        setSelectedSubClassificationTag(pleaseSelectLabel)
    }, [
        setWorkflowInput,
        setClassificationTagSelectedOption,
        setScheduleName,
        setCronExpression,
        setSelectedSubClassificationTag
    ])

    const onSubClassificationTagChangeHandler = useCallback((detail: SelectProps.ChangeDetail) => {
        setSelectedSubClassificationTag(detail.selectedOption)
    }, [setSelectedSubClassificationTag])

    const onDomainChangeHandler = useCallback((detail: BaseChangeDetail) => {
        setDomainName(detail.value.trim())
    }, [setDomainName])

    const onTargetWebsiteChange = useCallback((detail: SelectProps.ChangeDetail) => {
        setTargetWebsite(detail.selectedOption)
    }, [setTargetWebsite])

    const onScheduleNameChange = useCallback((scheduleName: string) => {
        setScheduleName(scheduleName)
    }, [setScheduleName])

    const onCronExpressionChange = useCallback((cronExpression: string) => {
        setCronExpression(cronExpression)
    }, [setCronExpression])

    const onScheduleStatusChange = useCallback((scheduleStatus: boolean) => {
        setIsScheduled(scheduleStatus);
    }, [setIsScheduled])

    const updateWorkflowLoadingStatus = useCallback((workflowLoadingStatus: boolean) => {
        setStartWFLoading(workflowLoadingStatus);
    }, [setStartWFLoading])

    const updateAdhocStartRecipeExecutionsResult = useCallback((adhocStartRecipeExecutionsResult: StartRecipeExecutionResultV1) => {
        setAdhocStartRecipeExecutionsResult(adhocStartRecipeExecutionsResult);
    }, [setAdhocStartRecipeExecutionsResult])

    return {
        schemaLoading,
        startWFLoading,
        workflowDetails,
        enableLiveValidate,
        workflowInput,
        adhocStartRecipeExecutionsResult,
        classificationTagSelectedOption,
        subClassificationTagOptions,
        selectedSubClassificationTag,
        domainName,
        classificationTagOptions,
        isContinuous,
        targetWebsite,
        targetWebsiteOptions,
        workflowUISchema,
        workflowDefaultLoading,
        workflowDefaultLoadError,
        retrieveSchemaForWorkflowDefinition,
        onTabFormDataChange,
        onTabFormDataError,
        errorText,
        itemsRef,
        errorListMap,
        onClassificationTagChangeHandler,
        onSubClassificationTagChangeHandler,
        onDomainChangeHandler,
        onTargetWebsiteChange,
        isScheduled,
        isScheduleDisabled,
        scheduleName,
        cronExpression,
        onScheduleNameChange,
        onCronExpressionChange,
        onScheduleStatusChange,
        resetFormValues,
        updateWorkflowLoadingStatus,
        updateAdhocStartRecipeExecutionsResult,
        onTabDataChangeHandler,
        onFormSubmitHandler,
        capabilitySelectedOption,
        capabilityOptions,
        primitiveSelectedOption,
        primitiveOptions,
        onCapabilityChangeHandler,
        onPrimitiveChangeHandler,
        workflowDefinitionOption,
        workflowDefinitionOptions,
        onWorkflowDefinitionChangeHandler
    }
}
